import React, { useState } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Button,
  Divider,
  Grid,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { useRouter } from 'next/router'
import AddToCartItemsMTO from '@src/routes/Configurators/components/AddToCart/AddToCartItemsMTORedesign'
import {
  GetProductInformationQuery,
  GetProductInformationDocument,
} from '@src/queries/ConfiguratorTubesQuery.generated'
import { useApolloClient } from '@apollo/react-hooks'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { sumBy } from 'lodash'
import useFormikHelper from '../useFormikHelper'
import { parseFloatPriceToNumber } from '../utils'
import {
  shouldRenderIsoCheckBox,
  SingleTubeFormValues,
  useProductFromQueryParam,
  useProductInput,
} from '../ProductsConfiguration'
import { useUserSession } from '@src/utils/useUserSession'
import { StandardConfiguratorAddToMiniCart } from '../../ConfiguratorSharedTypes'
import { QUICK_CART } from '@src/queries/QuickCartQuery'
import { CartRequestAction } from '@src/types/graphql-types'
import { useLabWaterParticipantId } from '@src/utils/useLabWaterParticipantId'
import {
  useConfAddToMiniCartMutation,
  useSessionFieldsMutation,
} from '@src/mutations/ConfiguratorTubesMutation.generated'
import { ConfiguratorValues } from '@src/routes/Configurators/utils/constants'
import {
  SnackbarType,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import LiquidCheckbox from '@src/components/LiquidCheckbox'
import { getSessionDataFromFormikData } from '../SessionDataUtil'
import { ActiveLinkType, TubeProduct } from '../../../utils/enum'
import { TubeTitleMap } from '../../TubesMTORedesign/enum'
import { BiTubeProduct } from '../../BiTube/enum'
import { sendAddToCartConfiguratorEvent } from '@src/utils/analytics/configuratorAnalytics'
import { priceFormatter } from '@src/utils/analytics/coreAnalytics'

const useStyles = makeStyles((theme: Theme) => ({
  containerWrapperA: {
    margin: theme.spacing(6, 0, 0),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  containerWrapperB: {
    margin: theme.spacing(6, 0, 0),
    display: 'flex',
    justifyContent: 'space-between',
  },
  content: {
    width: '40%',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
  },
  heading: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightBold,
  },
  divider: {
    backgroundColor: theme.palette.grey['500'],
    height: 1,
    margin: theme.spacing(2.75, 0),
    width: '100%',
  },
  noteInfo: {
    fontSize: theme.typography.pxToRem(12),
    paddingRight: theme.spacing(7.5),
  },
  addToCart: {
    margin: theme.spacing(6, 0),
  },
  isoCode: {
    marginTop: theme.spacing(6),
  },
  loadingIcon: {
    position: 'absolute',
    right: '5%',
    zIndex: 1,
    color: theme.palette.primary.main,
  },
  formControlLabelStyle: {
    width: 0,
    marginTop: theme.spacing(1.25),
  },
  noBorder: {
    marginTop: theme.spacing(1),
    '& td, & th': {
      border: 'none',
    },
  },
  noPaddingLeft: {
    paddingLeft: 0,
  },
  textLabel: {
    fontWeight: 'bold',
  },
}))

const purificationValueMap = {
  [BiTubeProduct.FastDuplex]: {
    id: 'DESALT',
    defaultMessage: 'Desalt',
  },
}

const formatValueMap = {
  [BiTubeProduct.FastDuplex]: {
    id: 'IN_SOLUTION',
    defaultMessage: 'In Solution',
  },
}

const hasQuoteItem = (items: SingleTubeFormValues[]): boolean => {
  return items.some(
    (data) =>
      typeof data.price === 'string' &&
      data.price.toLowerCase().includes('quote')
  )
}

const SubtotalAndAddToCart = ({ loading }: { loading?: boolean }) => {
  const classes = useStyles()
  const formik = useFormikHelper()
  const client = useApolloClient()
  const router = useRouter()
  const { userSession } = useUserSession()
  const {
    userIsLoggedIn,
    isCustomProductCSR,
    isCustomerServiceRep,
    isOrderableCustomer,
    isSAPNextEnabled,
  } = useCurrentUser()
  const product = useProductFromQueryParam()
  const [sessionFields] = useSessionFieldsMutation()
  const productInput = useProductInput()
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [addToCartLoading, setAddToCartLoading] = useState<boolean>(false)
  const [companionProducts, setCompanionProducts] = useState<
    (string | null)[] | null
  >(null)

  const isoCodeValue = formik.allValues?.isoCode === true
  const [isoCode, setISOCode] = useState<boolean>(isoCodeValue)
  const renderIsoCheckBox =
    isCustomProductCSR && shouldRenderIsoCheckBox(product)
  const [cartSuccessModelData, setCartSuccessModelData] = useState<{
    quantity: number
    price: string | JSX.Element
  }>({
    quantity: 0,
    price: '--',
  })
  const [addToMiniCart, { data }] = useConfAddToMiniCartMutation({})
  const participationId = useLabWaterParticipantId()
  const { setGlobalSnackbarState } = useGlobalSnackbar()
  const redirectToSequenceEntry = () => {
    formik.setFieldValue('activeOligoIndex', 0, false)
    router.push(
      {
        pathname: router.pathname,
        query: {
          country: router.query.country,
          language: router.query.language,
          product: router.query.product,
          activeLink: ActiveLinkType.SequenceEntry,
        },
      },
      undefined
    )
  }

  React.useEffect(() => {
    setAddToCartLoading(false)
    if (data) {
      if (data?.confAddToMiniCart?.serverError) {
        //error
        setGlobalSnackbarState({
          open: true,
          message: ConfiguratorValues.AddToCartFail,
          variant: SnackbarType.Error,
        })
      } else {
        if (data?.confAddToMiniCart?.result === 'success') {
          formik.removeItemFromOligoList(null, true, false)
          setDialogOpen(true)
        } else if (
          data?.confAddToMiniCart?.result === 'ERROR_SESSION_DATA_EMPTY'
        ) {
          setGlobalSnackbarState({
            open: true,
            message: ConfiguratorValues.AddToCartFail,
            variant: SnackbarType.Error,
          })
          formik.removeItemFromOligoList(null, true)
          redirectToSequenceEntry()
        } else {
          setGlobalSnackbarState({
            open: true,
            message: ConfiguratorValues.AddToCartFail,
            variant: SnackbarType.Error,
          })
        }
      }
    }
  }, [data])

  const getCompanionProducts = async () => {
    const { data } = await client.query<GetProductInformationQuery>({
      query: GetProductInformationDocument,
      variables: {
        input: {
          productType: product,
          countryCode: userSession.country,
          isCsr: isCustomerServiceRep,
          isCsrSupervisor: isCustomProductCSR,
          isOrderableCustomer,
          regUser: userIsLoggedIn,
          locale: `${userSession.language}_${userSession.country}`,
          sapNextEnabled: isSAPNextEnabled,
        },
      },
    })
    if (data?.getProductInformation?.companionProducts) {
      setCompanionProducts(data?.getProductInformation?.companionProducts)
    }
  }

  const disableAddToCart =
    formik.allValues.oligoList.filter((data) => data.sequence).length === 0 ||
    !!formik.allValues.oligoList.find(
      (oligo) => oligo?.listErrors?.length > 0
    ) ||
    loading

  const calculatePrice = () => {
    if (!formik?.allValues?.oligoList || disableAddToCart) {
      return '-'
    }
    if (hasQuoteItem(formik?.allValues?.oligoList)) {
      return <FormattedMessage id="QUOTE_ITEM" defaultMessage="Quote Item" />
    }
    const tableData = formik.allValues.oligoList
      .filter((data) => data.sequence)
      .map((item) => ({ ...item, price: parseFloatPriceToNumber(item.price) }))
    const totalPrice = sumBy(tableData, 'price')

    if (totalPrice !== 0) {
      return (
        <FormattedNumber
          style="currency"
          currencyDisplay="symbol"
          currency={formik.allValues.oligoList[0].currency}
          value={totalPrice}
        />
      )
    }
    return '-'
  }

  const handleISOCode = (event) => {
    const { checked } = event.target
    if (checked !== null || checked !== undefined) {
      setISOCode(checked)
      formik.setFieldValue('isoCode', checked, false)
      sessionFields({
        variables: {
          input: getSessionDataFromFormikData(
            product,
            formik.allValues.oligoList,
            checked
          ),
          productInput,
        },
      })
    }
  }

  const handleAddToCart = async () => {
    setAddToCartLoading(true)
    const date = new Date()
    const timeZone = (date.getTimezoneOffset() / 60) * -1
    setCartSuccessModelData({
      quantity: formik.allValues?.oligoList?.length || 0,
      price: calculatePrice(),
    })
    const configuratorAddToMiniCart: StandardConfiguratorAddToMiniCart = {
      productType: product,
      countryCode: userSession.country,
      participantId: participationId,
      locale: `${userSession.language}_${userSession.country}`,
      sapNextEnabled: isSAPNextEnabled,
      timeZone: timeZone.toString(),
      store: userSession.store,
    }

    await addToMiniCart({
      variables: { input: configuratorAddToMiniCart },
      fetchPolicy: 'no-cache',
      refetchQueries: [
        {
          query: QUICK_CART,
          variables: { id: CartRequestAction.Mini },
        },
      ],
    })

    getCompanionProducts()
    const googleAnalyticsItems = formik.allValues.oligoList.map((item) => ({
      id: item.material?.toLowerCase() || '',
      name:
        TubeTitleMap[product]?.defaultMessage.replace(/[©®™]/g, '').trim() ||
        product,
      brand: item.brand?.toLowerCase() || '',
      price: priceFormatter(item.price),
      quantity: 1,
      materialNumber: item.material,
      dimension91: 'standard',
      variant: item.material?.toLowerCase() || '',
    }))

    sendAddToCartConfiguratorEvent(
      googleAnalyticsItems,
      formik.allValues.oligoList[0].currency
    )
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    //redirect
    redirectToSequenceEntry()
  }

  const cartEditItemPresent = formik.allValues.oligoList.some(
    (item) => item.cartId
  )
  const displaySeparateSpecifications =
    product === TubeProduct.iscaledna ||
    product === TubeProduct.Probe ||
    product === BiTubeProduct.LightCycler ||
    product === BiTubeProduct.FastDuplex ||
    product === BiTubeProduct.mirna

  const displayPurification =
    product === BiTubeProduct.LightCycler ||
    product === BiTubeProduct.FastDuplex

  const displayTubes = product === BiTubeProduct.FastDuplex

  const SpecificationRow = ({
    labelId,
    labelDefaultMessage,
    valueId,
    valueDefaultMessage,
  }) => (
    <TableRow>
      <TableCell className={classes.noPaddingLeft}>
        <Typography variant="body2" className={classes.textLabel}>
          <FormattedMessage id={labelId} defaultMessage={labelDefaultMessage} />
          :
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          <FormattedMessage id={valueId} defaultMessage={valueDefaultMessage} />
        </Typography>
      </TableCell>
    </TableRow>
  )

  return (
    <div
      className={
        displaySeparateSpecifications
          ? classes.containerWrapperB
          : classes.containerWrapperA
      }
    >
      {displaySeparateSpecifications && (
        <Grid item>
          <Typography className={classes.heading}>
            <FormattedMessage
              id="SPECIFICATIONS"
              defaultMessage="Specifications"
            />
          </Typography>
          <Table size="small" className={classes.noBorder}>
            <TableBody>
              {displayPurification && (
                <SpecificationRow
                  labelId="PURIFICATION"
                  labelDefaultMessage="Purification"
                  valueId={purificationValueMap[product]?.id || 'HPLC'}
                  valueDefaultMessage={
                    purificationValueMap[product]?.defaultMessage || 'HPLC'
                  }
                />
              )}
              <SpecificationRow
                labelId="FORMAT"
                labelDefaultMessage="Format"
                valueId={formatValueMap[product]?.id || 'DRY'}
                valueDefaultMessage={
                  formatValueMap[product]?.defaultMessage || 'Dry'
                }
              />
              {displayTubes && (
                <SpecificationRow
                  labelId="TUBES"
                  labelDefaultMessage="Tubes"
                  valueId="1"
                  valueDefaultMessage="1"
                />
              )}
            </TableBody>
          </Table>
        </Grid>
      )}
      <Grid container className={classes.content}>
        <Grid item className={classes.heading}>
          <FormattedMessage id="SUBTOTAL" defaultMessage="Subtotal" />
          :&nbsp;
          <span>{calculatePrice()}</span>
        </Grid>
        <Divider className={classes.divider} />
        <Grid item className={classes.noteInfo}>
          <>
            <p>
              <FormattedMessage
                id="CONF_SHIPPING_AMBIENT_ROOM_TEMP"
                defaultMessage="Shipping: Ambient (Room Temperature)"
              />
            </p>

            <p>
              <FormattedMessage
                id="CONF_APPLICABLE_FREIGHT_TAX_AND_FEES_DURING_CHECKOUT_DNA_TUBE"
                defaultMessage="Applicable freight, taxes & fees will be applied during checkout"
              />
            </p>
            <p>
              <FormattedMessage
                id="CONF_PROMOTIONAL_CODES_CHECKOUT_DNA_TUBE"
                defaultMessage="Promotional codes can be applied during checkout"
              />
            </p>
          </>
        </Grid>
        {renderIsoCheckBox && (
          <Grid item className={classes.isoCode}>
            <LiquidCheckbox
              checked={isoCode}
              formControlClasses={{ label: classes.formControlLabelStyle }}
              onChange={handleISOCode}
              label={
                <FormattedMessage
                  id="CONF_ISO_CODE"
                  defaultMessage="ISO13485"
                />
              }
            />
          </Grid>
        )}
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            size="medium"
            className={classes.addToCart}
            disabled={disableAddToCart || addToCartLoading}
            onClick={handleAddToCart}
          >
            {cartEditItemPresent ? (
              <FormattedMessage id="UPDATE_CART" defaultMessage="Update Cart" />
            ) : (
              <FormattedMessage id="ADD_TO_CART" defaultMessage="Add to Cart" />
            )}
            {addToCartLoading && (
              <CircularProgress size={20} className={classes.loadingIcon} />
            )}
          </Button>
        </Grid>
        <AddToCartItemsMTO
          dialogOpen={dialogOpen}
          handleDialogClose={handleDialogClose}
          price={cartSuccessModelData.price}
          totalItem={cartSuccessModelData.quantity}
          companionProducts={companionProducts}
        />
      </Grid>
    </div>
  )
}

export default SubtotalAndAddToCart
